import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { UserMenu } from "@/components/UserMenu";
import { useAuth } from "@/context/AuthContext";
import { BotaoManutencao, FaixaManutencaoAdmin } from "@/components/Manutencao";
import { API } from "@/lib/api";
import { LayoutDashboard, BookOpen, Users, Globe, Settings, ChevronDown, Wallet, Award, Server, Trash2, ExternalLink, Scissors, FlaskConical, Menu, X } from "lucide-react";
import { NucleoIcone } from "@/components/InnovaIAAluna";

// Menu no estilo da plataforma antiga: seções com submenus.
// no celular, só estas ficam no menu de baixo (as outras vão para o botão "Mais")
const PRINCIPAIS_CELULAR = ["painel", "cursos", "usuarios", "financeiro"];

const sections = [
  { key: "painel", label: "Dashboard", icon: LayoutDashboard, to: "/admin", end: true, testid: "admin-nav-dashboard-link" },
  {
    key: "cursos", label: "Cursos", icon: BookOpen, children: [
      { to: "/admin/cursos", label: "Todos os cursos", testid: "admin-nav-courses-link" },
      { to: "/admin/modulos", label: "Módulos", testid: "admin-nav-modules-link" },
      { to: "/admin/biblioteca", label: "Biblioteca", testid: "admin-nav-library-link" },
      { to: "/admin/provas", label: "Provas", testid: "admin-nav-exams-link" },
      { to: "/admin/matriculas", label: "Matrículas", testid: "admin-nav-enrollments-link" },
      { to: "/admin/turmas", label: "Turmas", testid: "admin-nav-groups-link" },
      { to: "/admin/loja", label: "Loja e campanhas", testid: "admin-nav-shop-link" },
    ],
  },
  {
    key: "usuarios", label: "Usuários", icon: Users, children: [
      { to: "/admin/alunos", label: "Alunos", testid: "admin-nav-students-link" },
      { to: "/admin/professores", label: "Professores", testid: "admin-nav-instructors-link" },
      { to: "/admin/avisos", label: "Convites e avisos", testid: "admin-nav-notices-link" },
      { to: "/admin/contratos", label: "Contratos", testid: "admin-nav-contracts-link" },
    ],
  },
  {
    key: "certificados", label: "Certificados", icon: Award, children: [
      { to: "/admin/certificados/emitidos", label: "Emitidos", testid: "admin-nav-cert-issued-link" },
      { to: "/admin/certificados/modelos", label: "Modelos", testid: "admin-nav-cert-templates-link" },
    ],
  },
  {
    key: "financeiro", label: "Financeiro", icon: Wallet, children: [
      { to: "/admin/financeiro/cobrancas", label: "Cobranças", testid: "admin-nav-charges-link" },
      { to: "/admin/financeiro/transacoes", label: "Transações", testid: "admin-nav-transactions-link" },
      { to: "/admin/financeiro/vendas", label: "Vendas", testid: "admin-nav-sales-link" },
      { to: "/admin/financeiro/links", label: "Links de venda", testid: "admin-nav-salelinks-link" },
      { to: "/admin/financeiro/bonus", label: "Taxas de bônus", testid: "admin-nav-bonusfees-link" },
      { to: "/admin/financeiro/notas", label: "Notas fiscais", testid: "admin-nav-nfse-link" },
      { to: "/admin/financeiro/recebedores", label: "Recebedores", testid: "admin-nav-recipients-link" },
      { to: "/admin/financeiro/configuracoes", label: "Configurações", testid: "admin-nav-finance-settings-link" },
    ],
  },
  { key: "salao", label: "IEP Salão", icon: Scissors, to: "/admin/salao", testid: "admin-nav-salao-gestao" },
  { key: "innova-ia", label: "IEP Intelligence", icon: NucleoIcone, to: "/admin/innova-ia", testid: "admin-nav-innova-ia" },
  { key: "tricolab", label: "Tricolab", icon: FlaskConical, to: "/admin/tricolab", testid: "admin-nav-tricolab" },
  {
    key: "site", label: "Site", icon: Globe, children: [
      { to: "/admin/site", label: "Páginas do site", testid: "admin-nav-site-link" },
      { href: "/profissionais", label: "Vitrine", testid: "admin-nav-vitrine-link" },   // página pública, abre em nova aba
    ],
  },
  { key: "servidor", label: "Servidor", icon: Server, to: "/admin/servidor", testid: "admin-nav-server-link" },
  { key: "lixeira", label: "Lixeira", icon: Trash2, to: "/admin/lixeira", testid: "admin-nav-trash-link" },
  {
    key: "config", label: "Configurações", icon: Settings, children: [
      { to: "/admin/escola", label: "Escola", testid: "admin-nav-school-link" },
      { to: "/admin/videos-ajuda", label: "Vídeos de ajuda", testid: "admin-nav-helpvideos-link" },
    ],
  },
];

const inSection = (sec, path) =>
  sec.children ? sec.children.some((c) => c.to && (path === c.to || path.startsWith(c.to + "/"))) : sec.end ? path === sec.to : path.startsWith(sec.to);

export default function AdminLayout() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const current = sections.find((s) => inSection(s, pathname));
  const [open, setOpen] = useState(() => new Set(current ? [current.key] : []));
  const [mais, setMais] = useState(false);     // celular: janela "Mais" com as outras seções
  useEffect(() => { setMais(false); }, [pathname]);

  // ao navegar, a seção atual fica aberta
  useEffect(() => {
    if (current?.children) setOpen((p) => new Set(p).add(current.key));
  }, [current?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key) => setOpen((p) => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const topCls = (active) =>
    `w-full flex items-center gap-3 px-4 py-3 rounded-lg text-[15px] font-medium transition-colors ${
      active ? "bg-gradient-to-r from-[#1E9E2A] to-[#168a22] text-white font-semibold shadow-[0_8px_20px_-10px_rgba(30,158,42,.9)]" : "text-slate-300 hover:bg-white/[.07] hover:text-white"
    }`;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <FaixaManutencaoAdmin />
      <header className="h-16 sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brand size="md" />
          <span className="hidden sm:inline-flex text-[10px] font-mono uppercase tracking-wider text-[#232D4F] bg-sky-50 border border-sky-100 px-2 py-1 rounded-md">
            Administração
          </span>
        </div>
        <UserMenu />
      </header>

      <div className="flex">
        <aside
          className="w-64 shrink-0 min-h-[calc(100vh-4rem)] p-3 space-y-1 hidden md:block sticky top-16 self-start max-h-[calc(100vh-4rem)] overflow-y-auto iep-scroll"
          style={{ background: "linear-gradient(180deg,#1A2340 0%,#232D4F 100%)" }}
          data-testid="admin-sidebar"
        >
          {sections.map((sec) => {
            const active = inSection(sec, pathname);
            if (!sec.children) {
              return (
                <NavLink key={sec.key} to={sec.to} end={sec.end} data-testid={sec.testid} className={() => topCls(active)}>
                  <sec.icon className="h-5 w-5" /> {sec.label}
                </NavLink>
              );
            }
            const isOpen = open.has(sec.key);
            return (
              <div key={sec.key}>
                <button onClick={() => toggle(sec.key)} className={topCls(active)} aria-expanded={isOpen} data-testid={`admin-nav-section-${sec.key}`}>
                  <sec.icon className="h-5 w-5" />
                  <span className="flex-1 text-left">{sec.label}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="mt-1 mb-2 ml-6 pl-3 border-l border-white/10 space-y-0.5">
                    {sec.children.map((c) => c.href ? (
                      <a key={c.href} href={c.href} target="_blank" rel="noopener" data-testid={c.testid}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm transition-colors text-slate-300 hover:text-white hover:bg-white/5">
                        {c.label} <ExternalLink className="h-3.5 w-3.5 opacity-60" />
                      </a>
                    ) : (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        data-testid={c.testid}
                        className={({ isActive }) =>
                          `block px-3 py-2 rounded-md text-sm transition-colors ${
                            isActive || pathname.startsWith(c.to + "/")
                              ? "text-white font-semibold bg-white/10 border-l-2 border-[#A7F3B0] -ml-[1px]"
                              : "text-slate-300 hover:text-white hover:bg-white/5"
                          }`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <a href="/cabeleireiro" target="_blank" rel="noopener" data-testid="admin-nav-cabeleireiro-link"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/90 hover:bg-white/10 transition-colors mt-3 border border-white/15">
            <ExternalLink className="h-5 w-5" /> Página do curso
          </a>
          <BotaoManutencao />
          {/* sistema do salão: abre já logado (só aparece com a integração configurada) */}
          {user?.salao && (
            <a href={`${API}/sso/salao`} target="_blank" rel="noopener" data-testid="admin-nav-salao-link"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#1E9E2A]/80 hover:bg-[#1E9E2A] transition-colors mt-2">
              <Scissors className="h-5 w-5" /> IEP Salão
            </a>
          )}
        </aside>

        {/* celular: só as seções principais embaixo; as outras ficam no botão "Mais" */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-t border-slate-200 flex pb-[env(safe-area-inset-bottom)]" data-testid="admin-menu-celular">
          {sections.filter((sec) => PRINCIPAIS_CELULAR.includes(sec.key)).map((sec) => {
            const active = inSection(sec, pathname);
            return (
              <NavLink key={sec.key} to={sec.to || sec.children[0].to} end={sec.end}
                className={() => `flex-1 min-w-0 flex flex-col items-center gap-1 py-2.5 text-[11px] ${active ? "text-[#1E9E2A] font-bold" : "text-slate-500"}`}>
                <sec.icon className="h-5 w-5" />
                <span className="truncate max-w-full px-1">{sec.key === "painel" ? "Início" : sec.label}</span>
              </NavLink>
            );
          })}
          <button type="button" onClick={() => setMais(true)} data-testid="admin-menu-mais"
            className={`flex-1 min-w-0 flex flex-col items-center gap-1 py-2.5 text-[11px] ${current && !PRINCIPAIS_CELULAR.includes(current.key) ? "text-[#1E9E2A] font-bold" : "text-slate-500"}`}>
            <Menu className="h-5 w-5" /> Mais
          </button>
        </div>
        {mais && (
          <div className="md:hidden fixed inset-0 z-[60] bg-[#0b1224]/50 backdrop-blur-[2px]" onClick={() => setMais(false)}>
            <div className="absolute bottom-0 left-0 right-0 rounded-t-3xl bg-white p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-2xl animate-fade-up" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <div className="font-display text-lg font-bold text-[#1A2340]">Todas as áreas</div>
                <button type="button" onClick={() => setMais(false)} className="h-9 w-9 grid place-items-center rounded-full bg-slate-100 text-slate-600" aria-label="Fechar"><X className="h-4 w-4" /></button>
              </div>
              <div className="grid grid-cols-3 gap-2.5">
                {sections.filter((sec) => !PRINCIPAIS_CELULAR.includes(sec.key)).map((sec) => {
                  const active = inSection(sec, pathname);
                  return (
                    <NavLink key={sec.key} to={sec.to || sec.children[0].to} end={sec.end}
                      className={() => `flex flex-col items-center justify-center gap-2 rounded-2xl border px-2 py-3.5 text-center text-[12px] font-semibold leading-tight ${active ? "border-[#1E9E2A] bg-green-50 text-[#14702a]" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                      <span className={`grid h-10 w-10 place-items-center rounded-xl ${active ? "bg-[#1E9E2A] text-white" : "bg-white text-[#232D4F] shadow-sm"}`}><sec.icon className="h-5 w-5" /></span>
                      {sec.label}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0 p-5 md:p-8 max-w-6xl 2xl:max-w-[1600px] mx-auto w-full pb-24 md:pb-8">
          {current?.children && (
            <div className="md:hidden flex gap-2 overflow-x-auto pb-3 mb-3 -mx-1 px-1">
              {current.children.map((c) => c.href ? (
                <a key={c.href} href={c.href} target="_blank" rel="noopener" className="shrink-0 px-3 py-1.5 rounded-full text-sm border bg-white text-slate-600 border-slate-200">{c.label}</a>
              ) : (
                <NavLink key={c.to} to={c.to}
                  className={({ isActive }) => `shrink-0 px-3 py-1.5 rounded-full text-sm border ${isActive ? "bg-[#232D4F] text-white border-[#232D4F]" : "bg-white text-slate-600 border-slate-200"}`}>
                  {c.label}
                </NavLink>
              ))}
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
